import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * HTTP request options
 */
export interface HttpOptions {
  params?: HttpParams | Record<string, string | number | boolean | string[]>;
  headers?: HttpHeaders | Record<string, string | string[]>;
  responseType?: 'json';
  withCredentials?: boolean;
}

/**
 * Extended options for requests that may include a body (DELETE with body)
 */
export interface HttpOptionsWithBody extends HttpOptions {
  body?: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private url: string;

  constructor(private http: HttpClient) {
    this.url = environment.api.url;
  }

  /**
   * Perform a GET request
   * @template T - Expected response type
   */
  get<T = unknown>(partial: string, options: HttpOptions = {}): Observable<T> {
    const callUrl = `${this.url}/${partial}`;
    return this.http.get<T>(callUrl, options).pipe(
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Perform a POST request
   * @template T - Expected response type
   */
  post<T = unknown>(partial: string, data: unknown, options: HttpOptions = {}): Observable<T> {
    const callUrl = `${this.url}/${partial}`;
    return this.http.post<T>(callUrl, data, options).pipe(
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Perform a POST request with upload progress reporting
   * @template T - Expected response type
   */
  postWithProgress<T = unknown>(partial: string, data: unknown): Observable<HttpEvent<T>> {
    const callUrl = `${this.url}/${partial}`;
    return this.http.post<T>(callUrl, data, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Perform a PUT request
   * @template T - Expected response type
   */
  put<T = unknown>(partial: string, data: unknown, options: HttpOptions = {}): Observable<T> {
    const callUrl = `${this.url}/${partial}`;
    return this.http.put<T>(callUrl, data, options).pipe(
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Perform a PATCH request
   * @template T - Expected response type
   */
  patch<T = unknown>(partial: string, data: unknown, options: HttpOptions = {}): Observable<T> {
    const callUrl = `${this.url}/${partial}`;
    return this.http.patch<T>(callUrl, data, options).pipe(
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Perform a DELETE request
   * @template T - Expected response type
   */
  delete<T = unknown>(partial: string, options: HttpOptionsWithBody = {}): Observable<T> {
    const callUrl = `${this.url}/${partial}`;
    return this.http.delete<T>(callUrl, options).pipe(
      catchError((error) => {
        throw error;
      })
    );
  }
}
